import React from "react";

// Stroke-style icon (heroicons-like outline paths), shared across the Automatic page.
export function StrokeIcon({ children, className = "h-4 w-4" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

// Keyword → icon lookup for solution tiles. Falls back to a DTC warning triangle
// for names that don't match a known category. Purpose-drawn glyphs per family.
export function SolutionIcon({ name, className }) {
  const n = String(name || "").toUpperCase();

  // DPF / CAT / GPF-OPF — soot-filter lamp: muffler with soot.
  if (n.includes("DPF") || n.includes("CAT") || n.includes("KAT") || n.includes("GPF") || n.includes("OPF")) {
    return (
      <StrokeIcon className={className}>
        <path d="M2 12h3m14 0h3" />
        <rect x="5" y="8" width="14" height="8" rx="3" />
        <circle cx="9" cy="12" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="15" cy="12" r="0.9" fill="currentColor" stroke="none" />
      </StrokeIcon>
    );
  }

  // EGR / SAP — check-engine lamp: the engine-block symbol.
  if (n.includes("EGR") || n.includes("SAP")) {
    return (
      <StrokeIcon className={className}>
        <path d="M3 10v6m9-11v3m-2-3h4m-9 8H3m3-3h2l2-2h3.382a1 1 0 0 1 .894.553l1.448 2.894a1 1 0 0 0 .894.553H18v-2h2a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-2v-2h-3v2a1 1 0 0 1-1 1h-3.465a1 1 0 0 1-.832-.445L8 16H6z" />
      </StrokeIcon>
    );
  }

  // Lambda / NOx — sensor signal pulse trace.
  if (n.includes("LAMBDA") || n.includes("NOX")) {
    return (
      <StrokeIcon className={className}>
        <path d="M3 12h4l3 8 4-16 3 8h4" />
      </StrokeIcon>
    );
  }

  // AdBlue / water pump — fluid droplet.
  if (n.includes("ADBLUE") || n.includes("WATER PUMP")) {
    return (
      <StrokeIcon className={className}>
        <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5-2 1.6-3 3.5-3 5.5a7 7 0 0 0 7 7Z" />
      </StrokeIcon>
    );
  }

  // EVAP — evaporative emissions vapor cloud.
  if (n.includes("EVAP")) {
    return (
      <StrokeIcon className={className}>
        <path d="M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
      </StrokeIcon>
    );
  }

  // Immo — key.
  if (n.includes("IMMO")) {
    return (
      <StrokeIcon className={className}>
        <path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777Zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4" />
      </StrokeIcon>
    );
  }

  // Start-Stop — power symbol.
  if (n.includes("START-STOP")) {
    return (
      <StrokeIcon className={className}>
        <path d="M12 2v10m6.4-7.4a9 9 0 1 1-12.77.04" />
      </StrokeIcon>
    );
  }

  // Glow plug / hot start — flame.
  if (n.includes("GLOW") || n.includes("HOTSTART")) {
    return (
      <StrokeIcon className={className}>
        <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5Z" />
      </StrokeIcon>
    );
  }

  // Swirl flaps / grill shutter / cylinder-on-demand / exhaust valve — airflow.
  if (
    n.includes("FLAPS") ||
    n.includes("SWIRL") ||
    n.includes("GRILL") ||
    n.includes("CYLINDER") ||
    n.includes("EXHAUST")
  ) {
    return (
      <StrokeIcon className={className}>
        <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2m7.6-7.4A2 2 0 1 1 11 8H2m10.6 11.4A2 2 0 1 0 14 16H2" />
      </StrokeIcon>
    );
  }

  // MAF — layered airflow waves.
  if (n.includes("MAF")) {
    return (
      <StrokeIcon className={className}>
        <path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
      </StrokeIcon>
    );
  }

  // Torque monitor — wrench.
  if (n.includes("TORQUE")) {
    return (
      <StrokeIcon className={className}>
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z" />
      </StrokeIcon>
    );
  }

  // Vmax / RPM limiter / readiness / display / pressure calibration — speedometer.
  if (
    n.includes("VMAX") ||
    n.includes("RPM") ||
    n.includes("READINESS") ||
    n.includes("SPORT DISPLAYS") ||
    n.includes("TPROT") ||
    n.includes("TVA") ||
    n.includes("OIL PRESSURE")
  ) {
    return (
      <StrokeIcon className={className}>
        <path d="m12 14 4-4M3.34 19a10 10 0 1 1 17.32 0" />
      </StrokeIcon>
    );
  }

  // Default — DTC warning triangle.
  return (
    <StrokeIcon className={className}>
      <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    </StrokeIcon>
  );
}

// One purpose-drawn glyph per tuning, same art style as SolutionIcon.
export function TuningIcon({ name, className }) {
  const s = String(name || "").toUpperCase();

  // Stage1 — a single rising arrow: the base tune.
  if (s === "STAGE1") {
    return (
      <StrokeIcon className={className}>
        <path d="M12 19V5m0 0-5 5m5-5 5 5" />
      </StrokeIcon>
    );
  }

  // Stage1+ — the same rising arrow with a plus, one notch above Stage1.
  if (s === "STAGE1+") {
    return (
      <StrokeIcon className={className}>
        <path d="M9 19V7m0 0-4 4m4-4 4 4" />
        <path d="M18 5v6m-3-3h6" />
      </StrokeIcon>
    );
  }

  // Stage2 — two rising arrows: the next tier up from Stage1+.
  if (s === "STAGE2") {
    return (
      <StrokeIcon className={className}>
        <path d="M8 19V9m0 0-3.5 3.5M8 9l3.5 3.5" />
        <path d="M16 19V5m0 0-3.5 3.5M16 5l3.5 3.5" />
      </StrokeIcon>
    );
  }

  // Pops & Bangs (switchable gets the same tailpipe-pop plus a toggle capsule).
  if (s.includes("POPS") || s.includes("BANGS") || s.includes("FART")) {
    return (
      <StrokeIcon className={className}>
        <rect x="2" y="10" width="7" height="4" rx="2" />
        <path d="M11 12h1.5m1.5-3 1.2 1.2M15.5 12H18m-2.3 3 1.2 1.2" />
        {s.includes("SWITCHABLE") ? (
          <>
            <rect x="19" y="9" width="5" height="6" rx="3" />
            <circle cx="22.5" cy="10.5" r="1" fill="currentColor" stroke="none" />
          </>
        ) : null}
      </StrokeIcon>
    );
  }

  // Launch Control Activation — a racing flag.
  if (s.includes("LAUNCH")) {
    return (
      <StrokeIcon className={className}>
        <path d="M5 21V3" />
        <path d="M5 4c2-1 4-1 6 0s4 1 6 0v6c-2 1-4 1-6 0s-4-1-6 0V4Z" />
      </StrokeIcon>
    );
  }

  // FCA Unlock — an open padlock.
  if (s.includes("FCA") || s.includes("UNLOCK")) {
    return (
      <StrokeIcon className={className}>
        <rect x="5" y="11" width="14" height="9" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 7.5-2" />
      </StrokeIcon>
    );
  }

  // E85 Conversion — a fuel nozzle.
  if (s.includes("E85") || s.includes("CONVERSION")) {
    return (
      <StrokeIcon className={className}>
        <path d="M4 21V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v15" />
        <path d="M4 21h8" />
        <path d="M14 8h2a2 2 0 0 1 2 2v7a1.5 1.5 0 0 0 3 0v-5" />
        <path d="M16 6l3 2" />
      </StrokeIcon>
    );
  }

  // Eco Tune — a leaf.
  if (s.includes("ECO")) {
    return (
      <StrokeIcon className={className}>
        <path d="M5 21c8 0 14-6 14-14V4h-3C8 4 5 10 5 18v3Z" />
        <path d="M5 21c2-6 6-10 12-12" />
      </StrokeIcon>
    );
  }

  // BETA scripts — a flask.
  if (s.includes("BETA")) {
    return (
      <StrokeIcon className={className}>
        <path d="M9 2h6" />
        <path d="M10 2v6.5L4.5 18a2 2 0 0 0 1.8 3h11.4a2 2 0 0 0 1.8-3L14 8.5V2" />
        <path d="M7 15h10" />
      </StrokeIcon>
    );
  }

  // HardCut — a lightning bolt (ignition cut).
  if (s.includes("HARDCUT") || s.includes("HARD CUT")) {
    return (
      <StrokeIcon className={className}>
        <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" />
      </StrokeIcon>
    );
  }

  // Default — a single ascending bar, for any tuning name not covered above.
  return (
    <StrokeIcon className={className}>
      <rect x="4" y="10" width="4" height="11" rx="1.5" fill="currentColor" stroke="none" />
      <rect x="10" y="6" width="4" height="15" rx="1.5" fill="currentColor" stroke="none" opacity="0.5" />
      <rect x="16" y="2" width="4" height="19" rx="1.5" fill="currentColor" stroke="none" opacity="0.25" />
    </StrokeIcon>
  );
}
